import { type FormEvent, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Input } from '@/shared/ui/Input';
import { Dialog } from '@/shared/ui/Dialog';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { Icon } from '@/shared/ui/Icon';
import { CalendarMultiSelect } from '@/shared/ui/CalendarMultiSelect';
import { TimeWheel } from '@/shared/ui/TimeWheel';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { CharSpringMorph } from '@/shared/ui/CharSpringMorph';
import { apiErrorMessage } from '@/shared/api/client';
import { useToast } from '@/shared/ui/Toast';
import {
  type DateCandidate,
  type DateVoteValue,
  type PlaceCandidate,
  type PlaceCategory,
  type PlaceTimeType,
  confirmDate,
  confirmPlace,
  createDateCandidate,
  createPlaceCandidate,
  getVoteSettings,
  listDateCandidates,
  listPlaceCandidates,
  unconfirmPlace,
  updateVoteSettings,
  voteDate,
  votePlace,
} from '@/shared/api/voting';
import { listScheduleComments, listScheduleItems } from '@/shared/api/schedule';
import { getMeeting, listParticipants } from '@/shared/api/meetings';
import { useAuthStore } from '@/shared/auth/store';
import { fmtKst } from '@/shared/lib/date';



const CATEGORY_LABEL: Record<PlaceCategory, { name: string; bg: string; fg: string }> = {
  MEAL: { name: '맛집', bg: 'var(--category-meal-bg)', fg: 'var(--category-meal-fg)' },
  CAFE: { name: '카페', bg: 'var(--category-cafe-bg)', fg: 'var(--category-cafe-fg)' },
  ACTIVITY: { name: '놀거리', bg: 'var(--category-activity-bg)', fg: 'var(--category-activity-fg)' },
  ETC: { name: '기타', bg: 'var(--category-default-bg)', fg: 'var(--category-default-fg)' },
};

function countVotes(summary: DateCandidate['voteSummary']): { yes: number; total: number } {
  if (!summary) return { yes: 0, total: 0 };
  return {
    yes: summary.YES ?? 0,
    total: (summary.YES ?? 0) + (summary.MAYBE ?? 0) + (summary.NO ?? 0),
  };
}

export default function VotePage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const voteKind = searchParams.get('tab') === 'place' || searchParams.get('schedule') ? 'place' : 'date';
  const selectedScheduleId = searchParams.get('schedule');
  const [confirmingDateId, setConfirmingDateId] = useState<string | null>(null);

  function switchVoteKind(next: 'date' | 'place') {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    if (next === 'date') params.delete('schedule');
    setSearchParams(params, { replace: true });
  }

  const meetingQ = useQuery({ queryKey: ['meeting', id], queryFn: () => getMeeting(id) });
  const partsQ = useQuery({
    queryKey: ['participants', id],
    queryFn: () => listParticipants(id),
    enabled: !!id,
  });
  const datesQ = useQuery({
    queryKey: ['dates', id],
    queryFn: () => listDateCandidates(id),
    enabled: !!id,
  });
  // 일정별 타임라인 뷰 — 전체 후보를 받아 일정 단위로 그룹핑한다
  const placesQ = useQuery({
    queryKey: ['places', id],
    queryFn: () => listPlaceCandidates(id),
    enabled: !!id,
  });
  const settingsQ = useQuery({
    queryKey: ['vote-settings', id],
    queryFn: () => getVoteSettings(id),
    enabled: !!id,
  });

  const isHost = user?.id === meetingQ.data?.hostUserId;
  const meetingStatus = meetingQ.data?.status;

  const scheduleQ = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => listScheduleItems(id),
    enabled: !!id,
  });
  const scheduleItems = scheduleQ.data ?? [];
  const selectedSchedule =
    scheduleItems.find((item) => item.id === selectedScheduleId) ?? scheduleItems[0] ?? null;
  const activeScheduleId = selectedSchedule?.id ?? selectedScheduleId;
  // 일정별 댓글 수 — 그룹 헤더에 표시 (MeetingPage와 같은 패턴)
  const commentCountsQ = useQueries({
    queries: scheduleItems.map((item) => ({
      queryKey: ['schedule-comments', item.id],
      queryFn: () => listScheduleComments(item.id),
      enabled: Boolean(item.id),
      staleTime: 30_000,
    })),
  });


  // 달력 다중 선택 — 'yyyy-MM-dd' 키 목록 + 모든 날짜 공통 시간
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [candHour, setCandHour] = useState(19);
  const [candMinute, setCandMinute] = useState(0);
  const [newPlaceName, setNewPlaceName] = useState('');
  const [newCategory, setNewCategory] = useState<PlaceCategory>('MEAL');
  const [newTimeType, setNewTimeType] = useState<PlaceTimeType>('ANYTIME');
  const [addingDate, setAddingDate] = useState(false);
  const [addingPlace, setAddingPlace] = useState(false);

  function toggleDay(key: string) {
    setSelectedDays((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key].sort(),
    );
  }

  function closeAddDate() {
    setAddingDate(false);
    setSelectedDays([]);
  }

  // 선택한 날짜들에 공통 시간을 붙여 후보를 일괄 생성 (기존 단건 API 반복 호출)
  const addDatesMut = useMutation({
    mutationFn: async () => {
      const isos = selectedDays.map((day) => {
        const d = new Date(`${day}T00:00:00`);
        d.setHours(candHour, candMinute, 0, 0);
        return d.toISOString();
      });
      // 순차 생성 — 첫 후보가 DRAFT→VOTING 전이를 일으키므로 동시 호출 시 경합(500)이 난다.
      for (const iso of isos) {
        await createDateCandidate(id, iso);
      }
      return isos.length;
    },
    onSuccess: (count) => {
      closeAddDate();
      qc.invalidateQueries({ queryKey: ['dates', id] });
      qc.invalidateQueries({ queryKey: ['meeting', id] });
      toast.success(`날짜 후보 ${count}개를 추가했어요.`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, '날짜 후보 추가에 실패했어요.')),
  });
  const voteDateMut = useMutation({
    mutationFn: (args: { candidateId: string; vote: DateVoteValue | null }) =>
      voteDate(args.candidateId, args.vote),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dates', id] }),
    onError: (err) => toast.error(apiErrorMessage(err, '투표에 실패했어요.')),
  });
  const confirmDateMut = useMutation({
    mutationFn: (candidateId: string) => confirmDate(id, candidateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meeting', id] });
      qc.invalidateQueries({ queryKey: ['dates', id] });
      setConfirmingDateId(null);
      toast.success('날짜를 확정했어요. 이제 장소를 정하고 일정을 만들어보세요.');
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, '날짜 확정에 실패했어요.'));
      qc.invalidateQueries({ queryKey: ['meeting', id] });
      qc.invalidateQueries({ queryKey: ['dates', id] });
    },
  });
  const addPlaceMut = useMutation({
    mutationFn: () =>
      createPlaceCandidate(id, {
        name: newPlaceName,
        category: newCategory,
        timeType: newTimeType,
        scheduleItemId: activeScheduleId ?? undefined,
      }),
    onSuccess: () => {
      setNewPlaceName('');
      setNewTimeType('ANYTIME');
      setAddingPlace(false);
      qc.invalidateQueries({ queryKey: ['places', id] });
      toast.success('장소 후보를 추가했어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '장소 후보 추가에 실패했어요.')),
  });
  const settingsMut = useMutation({
    mutationFn: (autoConfirmDate: boolean) => updateVoteSettings(id, { autoConfirmDate }),
    onSuccess: (s) => {
      qc.setQueryData(['vote-settings', id], s);
      toast.success(
        s.autoConfirmDate ? '전원 가능이면 날짜가 자동 확정돼요.' : '자동 확정을 껐어요.',
      );
    },
    onError: () => toast.error('설정 변경에 실패했어요.'),
  });
  const votePlaceMut = useMutation({
    mutationFn: (args: { placeCandidateId: string; yes: boolean }) =>
      votePlace(args.placeCandidateId, args.yes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['places', id] }),
    onError: (err) => toast.error(apiErrorMessage(err, '투표에 실패했어요.')),
  });
  const confirmPlaceMut = useMutation({
    mutationFn: (args: { placeCandidateId: string; confirmed: boolean }) =>
      args.confirmed ? unconfirmPlace(args.placeCandidateId) : confirmPlace(args.placeCandidateId),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['places', id] });
      qc.invalidateQueries({ queryKey: ['schedule', id] });
      toast.success(args.confirmed ? '장소 확정을 해제했어요.' : '이 일정의 장소를 확정했어요.');
    },
    onError: (err) => toast.error(apiErrorMessage(err, '장소 확정에 실패했어요.')),
  });

  function onAddPlace(e: FormEvent) {
    e.preventDefault();
    addPlaceMut.mutate();
  }


  // 일정 id → 그 일정의 장소 후보들. 일정 미지정 후보는 UNASSIGNED 키로 모은다.
  const UNASSIGNED = '__unassigned__';
  const placesBySchedule = useMemo(() => {
    const map = new Map<string, PlaceCandidate[]>();
    for (const p of placesQ.data ?? []) {
      const key = p.scheduleItemId ?? UNASSIGNED;
      const list = map.get(key);
      if (list) list.push(p);
      else map.set(key, [p]);
    }
    return map;
  }, [placesQ.data]);
  const unassignedPlaces = placesBySchedule.get(UNASSIGNED) ?? [];

  // 투표 마감용 1위 후보 — '가능' 최다, 동률이면 빠른 날짜
  const topDate = useMemo<DateCandidate | null>(() => {
    const list = datesQ.data ?? [];
    if (list.length === 0) return null;
    return [...list].sort((a, b) => {
      const diff = countVotes(b.voteSummary).yes - countVotes(a.voteSummary).yes;
      if (diff !== 0) return diff;
      return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
    })[0];
  }, [datesQ.data]);

  const confirmingDate = (datesQ.data ?? []).find((c) => c.id === confirmingDateId) ?? null;

  const participantCount = partsQ.data?.filter((p) => !p.leftAt).length ?? 0;
  const totalPlaceCount = placesQ.data?.length ?? 0;
  function placeAddPathFor(scheduleItemId: string | null): string {
    return `/meetings/${id}/places?from=vote&mode=map${scheduleItemId ? `&schedule=${scheduleItemId}` : ''}`;
  }

  return (
    <section className="space-y-4 bg-canvas pb-4">
      <h1 className="sr-only">투표</h1>
      <MeetingTabs meetingId={id} />

      <div className="grid grid-cols-2 gap-1 rounded-full border border-surface-border/70 bg-surface/70 p-1 backdrop-blur">
        <button
          type="button"
          onClick={() => switchVoteKind('date')}
          className={`min-h-12 rounded-full text-[14px] font-semibold transition-colors ${voteKind === 'date' ? 'bg-[image:var(--hero-grad)] text-white shadow-[var(--hero-glow)]' : 'text-ink-muted'}`}
        >
          날짜투표
        </button>
        <button
          type="button"
          onClick={() => switchVoteKind('place')}
          className={`min-h-12 rounded-full text-[14px] font-semibold transition-colors ${voteKind === 'place' ? 'bg-[image:var(--hero-grad)] text-white shadow-[var(--hero-glow)]' : 'text-ink-muted'}`}
        >
          장소투표
        </button>
      </div>
      <section
        aria-label="날짜투표"
        className={`overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm ${voteKind === 'date' ? '' : 'hidden'}`}
      >
        <p className="px-4 pb-1 pt-3 text-small font-medium text-ink-muted">
          {(datesQ.data ?? []).length}개 후보 · {participantCount}명 참여
        </p>

        {datesQ.isLoading ? (
          <div className="px-4 pb-4">
            <SkeletonCard />
          </div>
        ) : null}
        {datesQ.isError ? (
          <p className="px-4 pb-4 text-body text-error" role="alert">
            날짜 후보를 불러오지 못했어요.
          </p>
        ) : null}

        {datesQ.data && datesQ.data.length === 0 ? (
          <div className="mx-4 mb-4 rounded-2xl bg-surface-muted px-4 py-5 text-center">
            <p className="text-body font-semibold text-ink">아직 날짜 후보가 없어요</p>
            <p className="mt-1 text-small text-ink-muted">가능한 날짜를 추가하세요</p>
          </div>
        ) : null}

        {datesQ.data && datesQ.data.length > 0 ? (
          <div className="space-y-2 px-3 pb-3" aria-label="날짜 후보 목록">
            {datesQ.data.map((c) => {
              const { yes, total } = countVotes(c.voteSummary);
              const denom = Math.max(participantCount, total, 1);
              const checked = c.myVote === 'YES';
              const percent = Math.round((yes / denom) * 100);
              const isTop = topDate?.id === c.id && yes > 0;
              const isConfirmable = isHost && meetingStatus === 'VOTING';
              const isConfirmedDate =
                meetingQ.data?.confirmedDate != null &&
                new Date(meetingQ.data.confirmedDate).getTime() === new Date(c.startAt).getTime();

              return (
                <div
                  key={c.id}
                  className={`relative overflow-hidden rounded-[14px] border bg-surface-subtle ${
                    checked ? 'border-primary/70' : isTop ? 'border-primary/35' : 'border-surface-border'
                  }`}
                >
                  <div className="relative z-10 flex items-center gap-2 px-3 py-2.5">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      aria-label={`${fmtKst(c.startAt, 'M월 d일 (E) HH:mm')} 가능`}
                      disabled={voteDateMut.isPending}
                      onClick={() =>
                        voteDateMut.mutate({ candidateId: c.id, vote: checked ? null : 'YES' })
                      }
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-150 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                        checked ? 'border-primary bg-primary text-white' : 'border-surface-border bg-surface text-transparent'
                      }`}
                    >
                      <Icon name="check" size={15} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {isTop ? (
                          <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-white">
                            1위
                          </span>
                        ) : null}
                        <p className="truncate text-small font-semibold text-ink">
                          {fmtKst(c.startAt, 'M월 d일 (E) HH:mm')}
                        </p>
                      </div>
                      <p className="mt-0.5 text-tag text-ink-faint">
                        {denom}명 중 {yes}명 가능
                        {isConfirmedDate ? ' · 확정됨' : checked ? ' · 투표함' : ''}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 text-small font-semibold tabular-nums ${
                        isTop ? 'text-primary' : 'text-ink-faint'
                      }`}
                    >
                      {yes}/{denom}
                    </span>
                    {isConfirmable ? (
                      <button
                        type="button"
                        onClick={() => setConfirmingDateId(c.id)}
                        className="shrink-0 rounded-lg px-2.5 py-1.5 text-small font-semibold text-primary hover:bg-primary-soft focus-visible:outline-none focus-visible:shadow-focus-ring"
                      >
                        이 날짜로
                      </button>
                    ) : null}
                  </div>
                  <div className="h-1.5 bg-surface-muted" aria-hidden="true">
                    <div
                      className={`h-full rounded-r-full ${isTop ? 'bg-primary' : 'bg-primary/45'}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        <div className="border-t border-dashed border-surface-border px-3 py-3">
          <button
            type="button"
            onClick={() => setAddingDate(true)}
            className="pressable flex min-h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-primary/35 bg-primary-soft/35 px-3 text-small font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            <Icon name="plus" size={14} />
            날짜 후보 추가
          </button>
        </div>

        {isHost && meetingStatus === 'VOTING' && topDate ? (
          <div className="flex gap-2 border-t border-surface-border px-3 py-3">
            <button
              type="button"
              onClick={() => setConfirmingDateId(topDate.id)}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary-soft px-3 py-2 text-small font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              <Icon name="check" size={14} />
              1위로 확정
              {countVotes(topDate.voteSummary).yes > 0
                ? ` · ${countVotes(topDate.voteSummary).yes}명 가능`
                : ''}
            </button>
          </div>
        ) : null}

        {/* 방장 고급 옵션 — 본문 흐름을 막지 않게 섹션 맨 아래로 */}
        {isHost ? (
          <div className="flex items-center justify-between gap-3 border-t border-surface-border px-4 py-2.5">
            <p className="text-small font-medium text-ink-muted">전원 가능 시 자동 확정</p>
            <button
              type="button"
              role="switch"
              aria-checked={settingsQ.data?.autoConfirmDate ?? false}
              aria-label="전원 가능이면 자동 확정"
              disabled={settingsMut.isPending || settingsQ.isLoading}
              onClick={() => settingsMut.mutate(!(settingsQ.data?.autoConfirmDate ?? false))}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                settingsQ.data?.autoConfirmDate ? 'bg-primary' : 'bg-surface-border'
              }`}
            >
              <span
                aria-hidden="true"
                className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-airbnb ${
                  settingsQ.data?.autoConfirmDate ? 'left-6' : 'left-1'
                }`}
              />
            </button>
          </div>
        ) : settingsQ.data?.autoConfirmDate ? (
          <p className="border-t border-surface-border px-4 py-2.5 text-small font-medium text-ink-muted">
            전원 가능이면 날짜가 자동 확정돼요
          </p>
        ) : null}
      </section>

      <section aria-label="장소투표" className={voteKind === 'place' ? 'space-y-3' : 'hidden'}>
        <p className="px-1 text-small font-medium text-ink-muted">
          {scheduleItems.length > 0
            ? `${scheduleItems.length}개 일정 · ${(placesQ.data ?? []).length}개 후보`
            : '코스를 먼저 추가하세요'}
        </p>


        {placesQ.isLoading ? (
          <div className="pb-1">
            <SkeletonCard />
          </div>
        ) : null}
        {placesQ.isError ? (
          <p className="px-4 pb-4 text-body text-error" role="alert">
            장소 후보를 불러오지 못했어요.
          </p>
        ) : null}

        {scheduleItems.length === 0 && placesQ.data && totalPlaceCount === 0 ? (
          <div className="rounded-[16px] border border-dashed border-primary/35 bg-primary-soft/35 px-4 py-4 text-center">
            <p className="text-ui font-semibold text-ink">코스 먼저 만들기</p>
            <p className="mt-1 text-[12px] font-medium text-ink-muted">
              일정을 추가하면 일정마다 장소를 투표할 수 있어요.
            </p>
            <Link
              to={`/meetings/${id}/places`}
              className="mt-3 inline-flex min-h-10 items-center justify-center rounded-xl bg-primary px-4 text-[12px] font-semibold text-white focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              코스 만들기
            </Link>
          </div>
        ) : null}

        {/* 시간대별 코스 레일(좌) + 일정별 장소 투표(우) — 어떤 일정의 투표인지 구조로 보여준다 */}
        {scheduleItems.length > 0 ? (
          <ol
            className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3.5 pb-1.5 pt-4"
            aria-label="일정별 장소투표"
          >
            {scheduleItems.map((item, index) => {
              // 확정 후보는 항상 그룹 맨 위 — 나머지는 서버 순서 유지(투표 중 행 점프 방지)
              const group = [...(placesBySchedule.get(item.id) ?? [])].sort(
                (a, b) => Number(b.confirmed) - Number(a.confirmed),
              );
              const groupTopVotes = Math.max(...group.map((p) => p.voteCount), 0);
              const groupMax = Math.max(groupTopVotes, 1);
              const groupTopIds = new Set(
                group.filter((p) => p.voteCount > 0 && p.voteCount === groupTopVotes).map((p) => p.id),
              );
              const confirmedPlace = group.find((p) => p.confirmed) ?? null;
              const isLast = index === scheduleItems.length - 1 && unassignedPlaces.length === 0;
              const groupCommentCount = commentCountsQ[index]?.data?.length ?? 0;
              return (
                <li key={item.id} className="relative flex gap-2">
                  <div className="relative flex w-3 shrink-0 flex-col items-center" aria-hidden="true">
                    {isLast ? null : (
                      <span className="absolute bottom-[-8px] left-1/2 top-[15px] w-[2px] -translate-x-1/2 rounded-full bg-gradient-to-b from-primary/40 to-primary/15" />
                    )}
                    <span
                      className={`mt-[6px] h-[9px] w-[9px] rounded-full ${
                        confirmedPlace ? 'bg-success' : 'bg-primary'
                      }`}
                    />
                  </div>
                  <div className="w-10 shrink-0 pt-px text-right text-[11px] font-semibold tabular-nums leading-4 text-ink-faint">
                    {fmtKst(item.startAt, 'HH:mm')}
                  </div>
                  <div className="min-w-0 flex-1 pb-5">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/meetings/${id}/schedule/${item.id}#schedule-comments`}
                        className="min-w-0 flex-1 rounded-lg focus-visible:outline-none focus-visible:shadow-focus-ring"
                      >
                        <span className="block truncate text-[15px] font-bold leading-5 tracking-[-0.25px] text-ink">
                          {item.title}
                        </span>
                      </Link>
                      {groupCommentCount > 0 ? (
                        <Link
                          to={`/meetings/${id}/schedule/${item.id}#schedule-comments`}
                          className="shrink-0 rounded-full bg-surface-muted px-2 py-1 text-[11px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                        >
                          댓글 {groupCommentCount}
                        </Link>
                      ) : null}
                      {confirmedPlace ? (
                        <span className="shrink-0 rounded-full bg-success-soft px-2 py-1 text-[11px] font-semibold text-success-strong">
                          장소 확정
                        </span>
                      ) : (
                        <span className="shrink-0 text-[11px] font-medium text-ink-faint">
                          {group.length > 0 ? `${group.length}개 후보` : '후보 없음'}
                        </span>
                      )}
                    </div>

                    {group.length === 0 ? (
                      <p className="mt-2 rounded-xl bg-surface-muted px-3 py-2.5 text-[12px] font-medium text-ink-muted">
                        장소 후보 없음 — 이 일정에 갈 곳을 추가해보세요.
                      </p>
                    ) : (
                      <ul className="mt-2 space-y-1.5" aria-label={`${item.title} 장소 후보`}>
                        {group.map((p) => {
                          const percent = Math.round((p.voteCount / groupMax) * 100);
                          const isTopPlace = groupTopIds.has(p.id);
                          return (
                            <li
                              key={p.id}
                              className={`overflow-hidden rounded-[14px] border bg-surface ${
                                p.confirmed
                                  ? 'border-success/60'
                                  : p.myVote
                                    ? 'border-primary/50'
                                    : isTopPlace
                                      ? 'border-primary/35'
                                      : 'border-surface-border'
                              }`}
                            >
                              <div className="flex min-h-[52px] items-center gap-2 px-3 py-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    {isTopPlace ? (
                                      <span className="shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                        1위
                                      </span>
                                    ) : null}
                                    <p className="min-w-0 flex-1 truncate text-[14px] font-semibold text-ink">
                                      {p.name}
                                    </p>
                                  </div>
                                  <div className="mt-0.5 flex items-center gap-2">
                                    <div className="inline-flex items-center text-[11px] font-semibold text-ink-faint">
                                      <span className="mr-1">관심</span>
                                      <CharSpringMorph value={String(p.voteCount)} gap={0} className="inline-flex" />
                                      <span>명</span>
                                    </div>
                                    {isHost && (p.myVote || p.confirmed) ? (
                                      <button
                                        type="button"
                                        disabled={confirmPlaceMut.isPending}
                                        onClick={() =>
                                          confirmPlaceMut.mutate({
                                            placeCandidateId: p.id,
                                            confirmed: p.confirmed,
                                          })
                                        }
                                        className={`pressable inline-flex h-7 shrink-0 items-center rounded-lg px-2 text-[11px] font-semibold focus-visible:outline-none focus-visible:shadow-focus-ring disabled:opacity-50 ${
                                          p.confirmed
                                            ? 'bg-success-soft text-success'
                                            : 'bg-surface-muted text-ink-muted hover:bg-primary-soft hover:text-primary'
                                        }`}
                                      >
                                        {p.confirmed ? '확정 해제' : '이 장소로 확정'}
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  aria-label={
                                    p.myVote
                                      ? `${p.name} 관심 취소`
                                      : `${p.name} 관심 표시`
                                  }
                                  aria-pressed={p.myVote ?? false}
                                  onClick={() =>
                                    votePlaceMut.mutate({ placeCandidateId: p.id, yes: !p.myVote })
                                  }
                                  className={`pressable inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-200 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                                    p.myVote
                                      ? 'bg-primary text-white'
                                      : 'bg-surface-muted text-ink-muted hover:bg-primary-soft hover:text-primary'
                                  }`}
                                >
                                  <Icon name="check" size={17} />
                                </button>
                              </div>
                              <div className="h-1.5 bg-surface-muted" aria-hidden="true">
                                <div
                                  className={`h-full rounded-r-full ${
                                    p.confirmed
                                      ? 'bg-success'
                                      : isTopPlace
                                        ? 'bg-primary'
                                        : 'bg-primary/45'
                                  }`}
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    <Link
                      to={placeAddPathFor(item.id)}
                      className="pressable mt-2 flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-dashed border-primary/35 bg-primary-soft/35 text-[12px] font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
                    >
                      <Icon name="plus" size={13} />
                      장소 추가
                    </Link>
                  </div>
                </li>
              );
            })}

            {unassignedPlaces.length > 0 ? (
              <li className="relative flex gap-2">
                <div className="relative flex w-3 shrink-0 flex-col items-center" aria-hidden="true">
                  <span className="mt-[6px] h-[9px] w-[9px] rounded-full bg-ink-faint/60" />
                </div>
                <div className="w-10 shrink-0 pt-px text-right text-[11px] font-semibold leading-4 text-ink-faint">
                  —
                </div>
                <div className="min-w-0 flex-1 pb-5">
                  <p className="text-[15px] font-bold leading-5 tracking-[-0.25px] text-ink">
                    일정 미지정
                  </p>
                  <ul className="mt-2 space-y-1.5" aria-label="일정 미지정 장소 후보">
                    {unassignedPlaces.map((p) => (
                      <li
                        key={p.id}
                        className="flex min-h-[52px] items-center gap-2 rounded-[14px] border border-surface-border bg-surface px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[14px] font-semibold text-ink">{p.name}</p>
                          <div className="mt-0.5 flex items-center text-[11px] font-semibold text-ink-faint">
                            <span className="mr-1">관심</span>
                            <CharSpringMorph value={String(p.voteCount)} gap={0} className="inline-flex" />
                            <span>명</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label={p.myVote ? `${p.name} 관심 취소` : `${p.name} 관심 표시`}
                          aria-pressed={p.myVote ?? false}
                          onClick={() =>
                            votePlaceMut.mutate({ placeCandidateId: p.id, yes: !p.myVote })
                          }
                          className={`pressable inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-200 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                            p.myVote
                              ? 'bg-primary text-white'
                              : 'bg-surface-muted text-ink-muted hover:bg-primary-soft hover:text-primary'
                          }`}
                        >
                          <Icon name="check" size={17} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ) : null}
          </ol>
        ) : null}
      </section>

      <Dialog
        open={confirmingDateId !== null}
        title="이 날짜로 확정할까요?"
        description={
          confirmingDate
            ? `${fmtKst(confirmingDate.startAt, 'M월 d일 (E) HH:mm')} · 가능 ${
                countVotes(confirmingDate.voteSummary).yes
              }표 — 확정하면 투표가 마감되고 일정을 만들 수 있어요.`
            : '확정하면 투표가 마감되고 일정을 만들 수 있어요.'
        }
        confirmLabel="확정"
        onConfirm={() => confirmingDateId && confirmDateMut.mutate(confirmingDateId)}
        confirmDisabled={confirmDateMut.isPending}
        onCancel={() => setConfirmingDateId(null)}
      />

      <Dialog
        open={addingDate}
        title="날짜 후보 추가"
        confirmLabel={
          addDatesMut.isPending
            ? '추가 중…'
            : selectedDays.length > 1
              ? `${selectedDays.length}개 추가`
              : '추가'
        }
        confirmDisabled={addDatesMut.isPending || selectedDays.length === 0}
        onConfirm={() => addDatesMut.mutate()}
        onCancel={() => {
          if (addDatesMut.isPending) return;
          closeAddDate();
        }}
      >
        <div className="space-y-4">
          <p className="text-small text-ink-muted">
            가능한 날짜를 달력에서 여러 개 골라보세요. 시간은 고른 날짜 모두에 똑같이 적용돼요.
          </p>
          <CalendarMultiSelect selected={selectedDays} onToggle={toggleDay} />

          {/* 시간 — 선택한 날짜 공통 적용 (세로 휠, 1분 단위, 직접 입력 가능) */}
          <div className="border-t border-surface-border/60 pt-3">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-small font-semibold text-ink-muted">시간</p>
              <span className="text-small font-semibold text-ink tabular-nums">
                {selectedDays.length > 0
                  ? `${selectedDays.length}개 선택됨 · ${String(candHour).padStart(2, '0')}:${String(candMinute).padStart(2, '0')}`
                  : '날짜를 먼저 선택하세요'}
              </span>
            </div>
            <TimeWheel
              hour={candHour}
              minute={candMinute}
              onChange={(h, m) => {
                setCandHour(h);
                setCandMinute(m);
              }}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        open={addingPlace}
        title="장소 후보 추가"
        confirmLabel={addPlaceMut.isPending ? '추가 중…' : '추가'}
        confirmDisabled={addPlaceMut.isPending || newPlaceName.trim().length === 0}
        onConfirm={() => addPlaceMut.mutate()}
        onCancel={() => {
          if (addPlaceMut.isPending) return;
          setAddingPlace(false);
        }}
      >
        <form onSubmit={onAddPlace} className="space-y-4">
          <Input
            label="장소 이름"
            value={newPlaceName}
            onChange={(e) => setNewPlaceName(e.target.value)}
            required
            autoFocus
          />
          <p className="rounded-lg bg-surface-muted px-3 py-2 text-small text-ink-muted">
            이름이나 주소로 위치를 찾아 거리·이동 시간을 자동 계산해요. 정확한 위치는 지도에서
            추가하는 게 좋아요.
          </p>
          <label className="block text-small font-semibold text-ink">
            카테고리
            <select
              value={newCategory}
              onChange={(event) => setNewCategory(event.target.value as PlaceCategory)}
              className="mt-1 min-h-11 w-full rounded-xl border border-surface-border bg-surface px-3 text-body font-semibold text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              {(Object.keys(CATEGORY_LABEL) as PlaceCategory[]).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c].name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-small font-semibold text-ink">
            예약
            <select
              value={newTimeType}
              onChange={(event) => setNewTimeType(event.target.value as PlaceTimeType)}
              className="mt-1 min-h-11 w-full rounded-xl border border-surface-border bg-surface px-3 text-body font-semibold text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              <option value="ANYTIME">예약 불필요</option>
              <option value="RESERVATION_REQUIRED">예약 필수</option>
            </select>
          </label>
        </form>
      </Dialog>
    </section>
  );
}
